/**
 * sketchService - Serviço híbrido (Online & Offline-First) para Croquis 2D
 * Funciona conectado e desconectado, persistindo localmente no IndexedDB e
 * sincronizando automaticamente quando houver internet.
 */

import api from './api';
import {
  SketchRepository,
  SyncQueueManager,
  OfflineManager
} from './database';

const ENTITY_TYPES = SyncQueueManager.ENTITY_TYPES;
const ACTIONS = SyncQueueManager.ACTIONS;

const isOnline = () => {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

// Projetos de croquis
export const getAllProjects = async (filters = {}) => {
  if (!isOnline()) {
    console.log('[Offline] Buscando projetos de croquis localmente');
    // Em offline retorna lista ou projeto local
    return { success: true, data: [] };
  }

  try {
    const response = await api.get('/sketch/projects', { params: filters });
    return response.data;
  } catch (error) {
    console.warn('[sketchService] Erro ao buscar projetos online, tentando localmente:', error);
    return { success: true, data: [] };
  }
};

export const getProjectById = async (id) => {
  // Se estiver offline ou se o ID for local provisório
  if (!isOnline() || String(id).startsWith('local_')) {
    console.log('[Offline] Carregando croqui do IndexedDB:', id);
    const local = await SketchRepository.getProjectById(id);
    if (local) {
      return { success: true, data: local };
    }
  }

  try {
    const response = await api.get(`/sketch/projects/${id}`);
    if (response.data?.data) {
      // Salva cópia local para consulta offline posterior
      await SketchRepository.saveProject(response.data.data);
    }
    return response.data;
  } catch (error) {
    console.warn('[sketchService] Falha de rede ao buscar projeto, buscando offline no IndexedDB:', error);
    const local = await SketchRepository.getProjectById(id);
    if (local) {
      return { success: true, data: local };
    }
    throw error;
  }
};

export const createProject = async (projectData) => {
  if (!isOnline() || (projectData.survey_id && String(projectData.survey_id).startsWith('local_'))) {
    console.log('[Offline] Criando projeto de croqui localmente no IndexedDB');
    const localId = `local_sketch_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const record = {
      ...projectData,
      id: localId,
      localId,
      serverId: null,
      syncStatus: 'pending'
    };
    await SketchRepository.saveProject(record);
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_PROJECT, localId, ACTIONS.CREATE, record);

    return {
      success: true,
      data: record
    };
  }

  try {
    const response = await api.post('/sketch/projects', projectData);
    if (response.data?.data) {
      await SketchRepository.saveProject(response.data.data);
    }
    return response.data;
  } catch (error) {
    console.warn('[sketchService] Falha online ao criar projeto, fallback para IndexedDB:', error);
    const localId = `local_sketch_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const record = {
      ...projectData,
      id: localId,
      localId,
      serverId: null,
      syncStatus: 'pending'
    };
    await SketchRepository.saveProject(record);
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_PROJECT, localId, ACTIONS.CREATE, record);

    return {
      success: true,
      data: record
    };
  }
};

export const updateProject = async (id, projectData) => {
  // Salva SEMPRE localmente primeiro para garantir integridade offline
  await SketchRepository.updateProject(id, projectData);

  if (!isOnline() || String(id).startsWith('local_')) {
    console.log('[Offline] Croqui atualizado no IndexedDB e enfileirado para sync');
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_PROJECT, id, ACTIONS.UPDATE, { id, ...projectData });
    return { success: true, data: { id, ...projectData } };
  }

  try {
    const response = await api.put(`/sketch/projects/${id}`, projectData);
    return response.data;
  } catch (error) {
    console.warn('[sketchService] Falha de rede ao atualizar croqui no servidor, mantendo offline:', error);
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_PROJECT, id, ACTIONS.UPDATE, { id, ...projectData });
    return { success: true, data: { id, ...projectData } };
  }
};

export const deleteProject = async (id) => {
  if (!isOnline() || String(id).startsWith('local_')) {
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_PROJECT, id, ACTIONS.DELETE, { id });
    return { success: true };
  }

  try {
    const response = await api.delete(`/sketch/projects/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao deletar projeto:', error);
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_PROJECT, id, ACTIONS.DELETE, { id });
    return { success: true };
  }
};

// Elementos do croqui
export const saveElements = async (projectId, elements) => {
  // Salva no IndexedDB imediatamente
  await SketchRepository.saveElements(projectId, elements);

  if (!isOnline() || String(projectId).startsWith('local_')) {
    console.log('[Offline] Elementos do croqui salvos no IndexedDB');
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_ELEMENTS, projectId, ACTIONS.CREATE, { projectId, elements });
    return { success: true, count: elements.length };
  }

  try {
    const response = await api.post(`/sketch/projects/${projectId}/elements`, { elements });
    return response.data;
  } catch (error) {
    console.warn('[sketchService] Falha ao enviar elementos online, garantido no IndexedDB:', error);
    await SyncQueueManager.enqueue(ENTITY_TYPES.SKETCH_ELEMENTS, projectId, ACTIONS.CREATE, { projectId, elements });
    return { success: true, count: elements.length };
  }
};

// Snapshot de imagem do croqui
export const saveSnapshot = async (projectId, dataUrl) => {
  // Salva no IndexedDB
  await SketchRepository.saveSnapshot(projectId, dataUrl);

  if (!isOnline() || String(projectId).startsWith('local_')) {
    console.log('[Offline] Snapshot do croqui armazenado localmente');
    return { success: true };
  }

  try {
    const response = await api.post(`/sketch/projects/${projectId}/snapshot`, { dataUrl });
    return response.data;
  } catch (error) {
    console.warn('[sketchService] Falha ao enviar snapshot online, salvo no IndexedDB:', error);
    return { success: true };
  }
};

// Templates de módulos
export const getModuleTemplates = async (filters = {}) => {
  if (!isOnline()) {
    return { success: true, data: [] };
  }

  try {
    const response = await api.get('/sketch/templates', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar templates:', error);
    return { success: true, data: [] };
  }
};
